import * as React from 'react'
import Image from 'next/image'
import NextLink from 'next/link'
import getSingleBrandContent from '../../src/common/queries/getSingleBrandContent'
import HeadlineEyebrow from '../../src/common/components/marketing/headlines/HeadlineEyebrow'
import { styled, theme } from '../../src/stitches.config'
import { Text } from '../../src/common/components/elements/Text/Text'
import LinkButton from '../../src/common/components/marketing/buttons/LinkButton'
import BrandFAQs from '../../src/common/components/marketing/reviews/BrandFAQ'
import HubspotLeadsForm from '../../src/common/components/marketing/forms/HubspotLeadsForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft'
import { faStore } from '@fortawesome/pro-light-svg-icons/faStore'
import { faUserHeadset } from '@fortawesome/pro-light-svg-icons/faUserHeadset'
import { faDisplay } from '@fortawesome/pro-light-svg-icons/faDisplay'
import { faMobileScreenButton } from '@fortawesome/pro-light-svg-icons/faMobileScreenButton'
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard'
import { faCode } from '@fortawesome/pro-light-svg-icons/faCode'
import { GetStaticPropsContext } from 'next'
import { getAllBuilderBrands } from '../../src/common/queries/getBuilderBrands'

const Section = styled('div', {
    maxWidth: '87%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    '@sm': {
        maxWidth: '1200px',
    },
    '& h2': {
        marginTop: '1.25rem',
    },
})

const SpaceBetweenWrapper = styled('div', {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    margin: '4rem 0 4.5rem',
    '@sm': {
        flexDirection: 'row',
    },
    '& svg': {
        fontSize: '1.25rem',
    },
})

const FlexCenterWrapper = styled('div', {
    display: 'flex',
    alignItems: 'center',
    margin: '.75rem 0',
})

const FlexBox = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '90%',
    '& p': {
        marginLeft: '1rem',
        paddingBottom: '.25rem',
    },
})

const BlueLine = styled('div', {
    borderBottom: '2px solid $secondary',
    maxWidth: '5rem',
    marginBottom: '1.5rem',
    height: '2px',
})

const LeftSideWrapper = styled('div', {
    width: '100%',
    '& h1': {
        marginBottom: '.5rem',
    },
    '& p': {
        margin: '1.5rem 0',
        lineHeight: '1.75rem',
    },
    '.c-dpxHRI-clACtY-size-md': {
        maxWidth: '16rem',
    },
    '@sm': {
        width: '50%',
        maxWidth: '20rem',
        margin: '0 1rem 0 2rem',
        '& p': {
            margin: '1.5rem 0 2rem',
            lineHeight: '1.75rem',
            maxWidth: '100%',
        },
    },
    '@md': {
        maxWidth: '32rem',
        '& h1,p': {
            maxWidth: '95%',
        },
    },
})

const RightSideWrapper = styled('div', {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-end',
    marginTop: '3rem',
    '@sm': {
        width: '40%',
        marginTop: 'unset',
        margin: '0 1.5rem',
    },
})

const ImageWrapper = styled('div', {
    display: 'flex',
    maxWidth: '100%',
    marginBottom: '1rem',
    '& img': {
        objectFit: 'cover',
        width: '100%',
        height: 'auto',
    },
})

const GrayBackground = styled('div', {
    backgroundColor: '$lightest',
    width: '100%',
    padding: '5rem 0 3rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '@sm': {
        padding: '5rem 0',
    },
})

const GrayContentWrapper = styled('div', {
    maxWidth: '1200px',
    width: '87%',
    '& h2': {
        marginTop: '1.25rem',
    },
    '@sm': {
        padding: '0 2rem',
        width: '100%',
    },
})

const AllIcons = styled('div', {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    margin: '4rem auto 0rem',
    '@md': {
        width: '90%',
        marginTop: '6rem',
    },
})

const LargeIconWrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '45%',
    padding: '1rem .25rem 3rem',
    textAlign: 'center',
    '& p': { lineHeight: '1.25rem', maxWidth: '8rem' },
    '& svg': {
        fontSize: '3.25rem',
        marginBottom: '2rem',
        color: '$secondary',
    },
    '@sm': {
        '& svg': {
            fontSize: '3.75rem',
        },
    },
    '@md': {
        width: '19%',
    },
})

const SmallIconWrapper = styled('div', {
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
    color: '$white',
    height: '50px',
    borderRadius: '50%',
    padding: 0,
    '& svg': {
        fontSize: '1.5rem',
    },
})

const BackButton = styled('div', {
    display: 'inline-block',
    paddingBottom: '2em',
    cursor: 'pointer',
    '&:hover': {
        color: '$primary',
        textDecoration: 'underline',
    },
})

const styles = {
    gradient: {
        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    greenBackground: {
        background: theme.colors.success,
    },
    grayBackground: {
        background: theme.colors.mediumGray,
    },
    form: {
        width: '100%',
        maxWidth: '1300px',
        minHeight: '37.5rem',
        padding: 0,
    },
    paddingTop: {
        padding: '45px 0 0',
    },
    blueLine: {
        width: '70px',
        marginTop: '60px',
        marginBottom: '20px',
    },
    divText: {
        fontSize: '20px',
        marginTop: '1.75rem',
        marginBottom: '2rem',
        lineHeight: 1.5,
    },
}

export default function BrandPage({ brand }: any) {
    const hasEgift = brand?.data.hasEgift
    const hasPlastic = brand?.data.hasPlastic
    const brandName = brand?.data.name
    const h1Text = brand?.data.h1
    const gradientText = h1Text?.match(brandName)
    const [before, after] = h1Text?.split(gradientText)
    const h1GradientText = (
        <span>
            {before}
            <span style={styles.gradient} data-testid={'gradientText'}>
                {gradientText}
            </span>
            {after}
        </span>
    )
    const brandImage = brand?.data.image
    const heroContent = brand?.data.heroContent
    const faqContent = brand?.data.faqs
    const buttonUrl = brand?.data.heroButtonLinkUrl
    return (
        <>
            <GrayBackground css={styles.paddingTop}>
                <GrayContentWrapper css={styles.form}>
                    <HubspotLeadsForm />
                </GrayContentWrapper>
            </GrayBackground>
            <Section>
                <SpaceBetweenWrapper>
                    <LeftSideWrapper>
                        <NextLink href={'/brands'} passHref>
                            <BackButton>
                                <FontAwesomeIcon
                                    icon={faArrowLeft}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                &nbsp;Back
                            </BackButton>
                        </NextLink>
                        <BlueLine />
                        <h1 data-testid={'h1Text'}>{h1GradientText}</h1>
                        <div
                            data-testid={'h1BodyText'}
                            style={styles.divText}
                            dangerouslySetInnerHTML={{ __html: heroContent }}
                        />
                        <LinkButton
                            variant={'solid'}
                            label={'Get Started'}
                            url={buttonUrl ?? '/corporate/bulk-gift-cards'}
                            data-testid={'getStartedButton'}
                            textTransform={'uppercase'}
                            buttonWidth={'17rem'}
                        />
                    </LeftSideWrapper>
                    <RightSideWrapper>
                        <ImageWrapper>
                            <Image
                                src={brandImage}
                                height={400}
                                width={630}
                                alt={`${brandName} gift card`}
                                data-testid={'brandedCard'}
                            />
                        </ImageWrapper>
                        <FlexCenterWrapper>
                            <SmallIconWrapper
                                css={
                                    hasEgift
                                        ? styles.greenBackground
                                        : styles.grayBackground
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faMobileScreenButton}
                                    height={50}
                                    width={50}
                                    data-testid={'smallIcon'}
                                />
                            </SmallIconWrapper>
                            <FlexBox>
                                <Text
                                    as={'p'}
                                    style={{ fontWeight: 600 }}
                                    size={4}
                                    data-testid={'eGiftText'}
                                >
                                    {hasEgift ? 'Available ' : 'Unavailable '}{' '}
                                    as an eGift
                                </Text>
                                <Text
                                    as={'p'}
                                    size={2}
                                    data-testid={'eGiftText2'}
                                >
                                    {hasEgift
                                        ? 'Standard eGift delivery is instantaneous'
                                        : 'eGift cards are not available'}
                                </Text>
                            </FlexBox>
                        </FlexCenterWrapper>
                        <FlexCenterWrapper>
                            <SmallIconWrapper
                                css={
                                    hasPlastic
                                        ? styles.greenBackground
                                        : styles.grayBackground
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faCreditCard}
                                    height={50}
                                    width={50}
                                />
                            </SmallIconWrapper>
                            <FlexBox>
                                <Text
                                    as={'p'}
                                    style={{ fontWeight: 600 }}
                                    size={4}
                                >
                                    Physical gift cards{' '}
                                    {hasPlastic ? 'available' : 'unavailable'}
                                </Text>
                                <Text as={'p'} size={2}>
                                    {hasPlastic
                                        ? 'Standard plastic delivery is within 1-5 business days'
                                        : 'Plastic gift cards are not available'}
                                </Text>
                            </FlexBox>
                        </FlexCenterWrapper>
                    </RightSideWrapper>
                </SpaceBetweenWrapper>
            </Section>
            <GrayBackground>
                <GrayContentWrapper>
                    <HeadlineEyebrow label={'Make the perfect choice'} />
                    <h2>
                        An all-in-one platform for effortless business gifting.
                    </h2>
                    <AllIcons>
                        <LargeIconWrapper>
                            <FontAwesomeIcon
                                icon={faStore}
                                data-testid={'largeIcon'}
                            />
                            <Text as={'p'}>400+ national gift card brands</Text>
                        </LargeIconWrapper>
                        <LargeIconWrapper>
                            <FontAwesomeIcon icon={faUserHeadset} />
                            <Text as={'p'}>24/7 customer support</Text>
                        </LargeIconWrapper>
                        <LargeIconWrapper>
                            <FontAwesomeIcon icon={faCode} />
                            <Text as={'p'}>API integration available</Text>
                        </LargeIconWrapper>
                        <LargeIconWrapper>
                            <FontAwesomeIcon icon={faDisplay} />
                            <Text as={'p'}>
                                Free access to your own ordering portal
                            </Text>
                        </LargeIconWrapper>
                    </AllIcons>
                </GrayContentWrapper>
            </GrayBackground>
            {faqContent && (
                <Section>
                    <BrandFAQs
                        faqContent={faqContent}
                        buttonUrl={'/corporate/faqs'}
                    />
                </Section>
            )}
        </>
    )
}

export async function getStaticProps({
    params,
}: GetStaticPropsContext<{ slug: string }>) {
    const brandName = params?.slug
    let brand
    if (brandName) {
        const brandData = await getSingleBrandContent(brandName)
        brand = brandData.props.brand
    }

    return {
        notFound: !brand,
        props: {
            brand: brand || null,
            content: {
                data: {
                    title: `Buy ${brand?.name || 'merchant'} gift cards`,
                    description: `Buy ${brand?.name || 'merchant'} gift cards or eGift cards delivered fast.`,
                },
            },
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 5 seconds
        revalidate: true,
    }
}

export async function getStaticPaths() {
    const paths = await getAllBuilderBrands()

    return {
        paths: paths.map((path) => `/brands/${path.data.slug}`),
        fallback: 'blocking',
    }
}
